import { isPast, parseISO } from 'date-fns';
import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { useTranslation } from 'next-i18next';
// import Link from 'next/link';
import { useMemo, useState, VFC } from 'react';

import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import updateCreationReservedAt from '@/apis/aniftyBackend/creations/updateCreationReservedAt';
import { ListingType } from '@/apis/aniftyBackend/mintAsks/interface';
import CustomButton from '@/components/CustomButton';
import LinkButton from '@/components/CustomButton/LinkButton';
import ReleaseReminderSetButton from '@/components/ReleaseReminderSetButton';
import SellConfirmationModal from '@/components/SellConfirmationModal';
// import routes from '@/config/routes';
import { HOST_NAME } from '@/constants/hosts';
import useAccessToken from '@/hooks/useAccessToken';
import LockIcon from '@/icons/lock-icon.svg';
// import PenIcon from '@/icons/pen-icon.svg';
import TwitterIcon from '@/icons/twitter.svg';
import { captureException } from '@/utils/Sentry';

import styles from './ReservedItemOverlay.module.scss';

type Props = {
  creation: V2CreationData;
  listingData: {
    supply: number;
    price: number;
  } | null;
  reservedAt: string | Date;
  isMyItem: boolean;
  creatorUserName: string;
  listingType: ListingType;
};

const ReservedItemOverlay: VFC<Props> = ({
  creation,
  listingData,
  reservedAt: _reservedAt,
  isMyItem,
  creatorUserName,
  listingType,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { accessToken, requestSignature } = useAccessToken();

  const [isEditScheduleModalOpen, setIsEditScheduleModalOpen] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  if (typeof _reservedAt === 'string') {
    _reservedAt = parseISO(_reservedAt);
  }

  const [reservedAt, setReservedAt] = useState<Date>(_reservedAt);

  // Guess current user's time zone
  const currentUsersTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  let displayTimeZone = 'UTC';
  if (currentUsersTimeZone === 'Asia/Tokyo') {
    // Display in JST only if user is in Japan
    displayTimeZone = 'JST';
  }

  const zonedTime = utcToZonedTime(reservedAt, displayTimeZone);

  const reservationDate = useMemo(() => {
    // Guess current user's time zone
    return format(reservedAt, 'LLL d, yyyy', {
      timeZone: displayTimeZone,
    });
  }, [reservedAt, displayTimeZone]);

  const reservationTime = useMemo(() => {
    if (format(zonedTime, 'mm') === '00') {
      // Omit minutes if date is on hour
      return `at ${format(zonedTime, 'K a')} ${displayTimeZone}`;
    }
    return `at ${format(zonedTime, 'K:mm a')} ${displayTimeZone}`;
  }, [reservedAt, displayTimeZone]);

  const handleScheduleEdited = async (newReservedAt?: {
    timeZone: 'Asia/Tokyo' | 'UTC' | 'EST' | 'EDT';
    localDate: Date;
  }) => {
    if (!newReservedAt || isProcessing) {
      return;
    }

    const { localDate, timeZone } = newReservedAt;

    setIsProcessing(true);
    setError(null);

    if (!accessToken) {
      const signatureRequestResult = await requestSignature();
      if (!signatureRequestResult) {
        setError(t('signatureRejectedError'));
        setIsProcessing(false);
        return;
      }
    }

    try {
      // Convert local date to UTC date
      const utcDate = zonedTimeToUtc(localDate, timeZone);

      // Request API
      await updateCreationReservedAt(creation.creationID, utcDate);

      // Update reserved at
      setReservedAt(utcDate);

      // Close modal
      setIsEditScheduleModalOpen(false);
    } catch (err) {
      setError(t('serverError'));
      captureException(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const tweetPostURL = useMemo(() => {
    const tweetText =
      i18n.language === 'ja'
        ? `新しい作品がもうすぐ発売されます！\n最新情報をこちらからチェックしてください！ \n\n ${HOST_NAME}/ja/@${creatorUserName}\n\n #ANIFTY #nftart #NFT`
        : `Hey guys! I got a new artwork coming out soon. Be the first to find out:\n\n ${HOST_NAME}/@${creatorUserName}\n\n #ANIFTY #nftart #NFT`;

    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      tweetText
    )}`;
  }, [creatorUserName]);

  if (isPast(reservedAt)) {
    return null;
  }

  return (
    <div className={styles.reservedItem}>
      <LockIcon className={styles.lockIcon} />

      <div className={styles.releaseDate}>
        {t('reservedItemOverlay.heading')}
        <div className={styles.theDate}>
          {reservationDate}
          <span>{reservationTime}</span>
        </div>
      </div>

      {/* Show edit schedule button if current user is the creator */}
      {isMyItem && (
        <>
          <CustomButton
            variant="secondary"
            onClick={() => setIsEditScheduleModalOpen(true)}
          >
            {t('reservedItemOverlay.editSchedule')}
          </CustomButton>
          {/* todo: below is for the "Edit Details" button. commented out until fixed */}
          {/* <CustomButton variant="whiteOrange">
            <Link href={routes.editCreation(creation.creationID)}>
              <a className={styles.creationEditLink}>
                <PenIcon />
                {t('creationDetail.editDetails')}
              </a>
            </Link>
          </CustomButton> */}
          <SellConfirmationModal
            creation={creation}
            listingData={listingData}
            scheduleSellingEnabled
            initialSchedule={reservedAt}
            error={error}
            isOpen={isEditScheduleModalOpen}
            isProcessing={isProcessing}
            onRequestClose={() => setIsEditScheduleModalOpen(false)}
            onConfirm={handleScheduleEdited}
            listingType={listingType}
          />
        </>
      )}

      <ReleaseReminderSetButton creationID={creation.creationID} />

      <LinkButton
        className={styles.shareTwitterButton}
        variant="primaryOutlined"
        href={tweetPostURL}
        target="_blank"
        rel="noreferer"
      >
        <TwitterIcon className={styles.twitterIcon} />
        {t('reserveCompleteModal.shareTwitterButton')}
      </LinkButton>
    </div>
  );
};

export default ReservedItemOverlay;
